import { css } from "@emotion/react"
import { Icon } from "source/shared/components"
import { formattedCents, moneyInputSanitize } from "source/giving/utils/money"
import { Fund } from "source/giving/donate/form/types"
import { Designation } from "../types"
import { Select } from "@planningcenter/doxy-web"
import { render } from "@testing-library/react"

interface EditDesignationsProps {
  designations: Designation[]
  funds: Fund[]
  onChangeDesignations: (nextDesignations: Designation[]) => void
}

export default function EditDesignations({
  designations,
  funds,
  onChangeDesignations,
}: EditDesignationsProps) {
  const activeDesignations = designations

  function availableFundsFor(designation: Designation | null) {
    const fundIds = activeDesignations.map((d) => d.fund.id)
    return funds.filter(
      (f) => f.id === designation?.fund.id || fundIds.indexOf(f.id) < 0,
    )
  }

  function handleNewDesignationClick() {
    const fund = availableFundsFor(null)[0]
    if (!fund) return

    const nextDesignations: Designation[] = [
      ...activeDesignations,
      {
        id: null,
        fund: fund,
        amount: "0",
      },
    ]
    onChangeDesignations(nextDesignations)
  }

  function handleRemoveDesignation(index: number) {
    const nextDesignations = activeDesignations.filter((d, i) => i !== index)
    onChangeDesignations(nextDesignations)
  }

  const designationTotalCents = (designations: Designation[]): number => {
    const totalCents = designations.reduce((totalCents, designation) => {
      const amountInCents =
        Math.round(parseFloat(designation.amount) * 100) || 0
      return totalCents + amountInCents
    }, 0)

    return totalCents
  }

  function handleChangeAmount(
    event: React.ChangeEvent<HTMLInputElement>,
    index: number,
  ) {
    const amount = moneyInputSanitize(event.target.value)

    const nextDesignations = [...activeDesignations]
    nextDesignations[index] = {
      ...nextDesignations[index],
      amount: amount,
    }
    onChangeDesignations(nextDesignations)
  }

  function handleBlurAmount() {
    // TODO: validate total amount min/max
  }

  function handleChangeSelectedFund(
    event: React.ChangeEvent<HTMLSelectElement>,
    index: number,
  ) {
    const fund = funds.find(
      (f) => f.id.toString() === event.target.value.toString(),
    )
    if (!fund) return

    const nextDesignations = [...activeDesignations]
    nextDesignations[index] = {
      ...nextDesignations[index],
      fund: fund,
    }
    onChangeDesignations(nextDesignations)
  }

  function getDesignationFund(designation: Designation): Fund {
    return funds.find((f) => f.id === designation.fund.id) || ({} as Fund)
  }

  function renderAmountAndFundFields(designation: Designation, index: number) {
    const { description: fundDescription } = getDesignationFund(designation)

    return (
      <>
        <div
          className="f-2"
          css={css`
            @media (min-width: 600px) {
              max-width: 60%;
            },
          `}
        >
          <div>
            <label
              htmlFor={`designation_${index}_fund`}
              className="screen-reader-text"
              aria-label="Fund"
            >
              Fund
            </label>
            <Select
              id={`designation_${index}_fund`}
              value={designation.fund.id}
              onChange={(e) => handleChangeSelectedFund(e, index)}
            >
              {availableFundsFor(designation).map((fund) => (
                <Select.Option
                  key={fund.id}
                  value={fund.id}
                  label={fund.name}
                />
              ))}
            </Select>
          </div>
          {fundDescription && (
            <div className="d-f pt-1 px-1 ai-fs jc-sb">
              <span className="c-tint2 fs-13">
                {getDesignationFund(designation).description}
              </span>
            </div>
          )}
        </div>
        <div
          className="f-1@sm"
          css={css`
            @media (min-width: 600px) {
              min-width: 150px;
            },
          `}
        >
          <div className="prepend-label">
            <label
              htmlFor={`designation_${index}_amount`}
              className="label"
              css={css`
                background-color: transparent !important;
              `}
            >
              <span className="screen-reader-text">Amount</span>$
            </label>
            <input
              type="text"
              inputMode="decimal"
              id={`designation_${index}_amount`}
              onBlur={handleBlurAmount}
              onChange={(e) => handleChangeAmount(e, index)}
              placeholder="0"
              value={designation.amount}
              className="label-container ta-r"
              css={css`
                padding-left: 26px !important;
              `}
            />
          </div>
        </div>
      </>
    )
  }

  function renderNewDesignationLinkAndTotal() {
    return (
      <div
        className="d-f jc-sb ai-c"
        css={css`
          grid-template-columns: 1fr 1fr;
        `}
      >
        <div className="d-f ai-c">
          {activeDesignations.length < funds.length && (
            <button
              type="button"
              onClick={handleNewDesignationClick}
              className="minor-btn secondary-btn btn"
            >
              <span
                className="mr-4p"
                css={css`
                  font-size: 9px;
                `}
              >
                <Icon symbol="general#plus" aria-hidden />
              </span>
              Add donation
            </button>
          )}
        </div>
        <div>
          {activeDesignations.length > 1 && (
            <div
              css={css`
                @media (min-width: 600px) {
                  padding-inline: 12px;
                  margin-inline-end: 36px;
                  width: 200px;
                  display: flex;
                  justify-content: space-between;
                }
              `}
            >
              <span>Total:</span>{" "}
              {formattedCents(designationTotalCents(activeDesignations), {
                noCentsIfWhole: true,
              })}
            </div>
          )}
        </div>
      </div>
    )
  }

  function renderDestroyButton(designation: Designation, index: number) {
    return (
      <div className="d-f d-if@md jc-fe">
        <button
          type="button"
          onClick={() => handleRemoveDesignation(index)}
          className="clickable-icon text-btn c-ruby d-f ai-c jc-c has-tip"
          data-tip="Remove"
          aria-label="Remove donation designation"
          css={css`
            border-radius: 4px;
            @media (min-width: 600px) {
              margin-right: -8px;
              margin-left: -2px;
            }
          `}
        >
          <Icon symbol="general#x" />
          <span className="d-n@sm pl-4p" style={{ lineHeight: 0 }}>
            Remove
          </span>
        </button>
      </div>
    )
  }

  return (
    <div className="mb-2">
      {activeDesignations.map((designation, index) => (
        <div
          key={index}
          className="d-f fd-c fd-r@sm w-100% jc-sb ai-fs@sm g-1 mb-2"
        >
          <>
            {renderAmountAndFundFields(designation, index)}
            {activeDesignations.length > 1 &&
              renderDestroyButton(designation, index)}
          </>
        </div>
      ))}
      {renderNewDesignationLinkAndTotal()}
    </div>
  )
}
